import { html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import striptags from 'striptags';
import { TailwindElement } from '../../shared/tailwind.element';
import { type QuestionaireData, questionaireContext } from '../../context/context';
import { type ReportData } from '../../interfaces/interfaces';
import { apiService } from '../../services/apiService';
import { CustomEventNames } from '../../constants/custom-event-names';

@customElement('szw-ba-report')
export class SzwBaReport extends TailwindElement() {
	@consume({ context: questionaireContext, subscribe: true })
	@property({ attribute: false })
	data!: QuestionaireData;

	@state()
	isLoading = false;

	@property({ attribute: false })
	public report?: ReportData;

	async connectedCallback(): Promise<void> {
		super.connectedCallback();
		if (this.data != null && this.data.canOpenReport) {
			this.isLoading = true;
			const reportResponse = await apiService.openReport(this.data.responseCode);
			this.report = reportResponse;
			this.isLoading = false;
		}
	}

	openQuestion(questionId: string): void {
		const event = new CustomEvent(CustomEventNames.OpenQuestion, {
			detail: {
				responseCode: this.data.responseCode,
				questionId,
			},
			bubbles: true,
			composed: true,
		});
		this.dispatchEvent(event);
	}

	render(): TemplateResult {
		return html` <article class="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6">
				<h1>Uitkomst</h1>
				${this.isLoading
					? html`<div role="status" class="mb-6 flex animate-pulse flex-col gap-2">
							<div class="mb-2 h-4 max-w-xs rounded-full bg-grijs-2"></div>
							<div class="h-4 w-full rounded-full bg-grijs-2"></div>
							<div class="h-4 w-full rounded-full bg-grijs-2"></div>
							<div class="h-4 max-w-sm rounded-full bg-grijs-2"></div>
					  </div>`
					: null}
				${this.report?.isDeterminedByTotalScore ?? false
					? unsafeHTML(this.report?.pointRangeHtml ?? '')
					: null}	
				${this.report?.isDeterminedByTotalScore ?? false
					? html`<p>U heeft ${this.report?.totalScore} punten gescoord.</p>`
					: null}
				${unsafeHTML(this.report?.resultHtml ?? '')}
				${this.report?.isDeterminedByTotalScore ?? false ? unsafeHTML(this.report?.scoreTableHtml ?? '') : null}
				<hr class="border-grijs-2" />
				<h2 id="answerHeader">Antwoorden</h2>
				${this.isLoading
					? html`<div role="status" class="mb-6 flex animate-pulse flex-col gap-6">
							<div>
								<div class="mb-2 h-4 max-w-xs rounded-full bg-grijs-6"></div>
								<div class="h-4 w-full rounded-full bg-grijs-2"></div>
							</div>
							<div>
								<div class="mb-2 h-4 max-w-xs rounded-full bg-grijs-6"></div>
								<div class="h-4 w-full rounded-full bg-grijs-2"></div>
							</div>
							<div>
								<div class="mb-2 h-4 max-w-xs rounded-full bg-grijs-6"></div>
								<div class="h-4 w-full rounded-full bg-grijs-2"></div>
							</div>
					  </div>`
					: null}
				<dl aria-labelledby="answerHeader">
					${this.report?.sections.map(
						(section) => html`
							${section.answers.map(
								(answer) =>
									html` <dt class="font-bold">${striptags(answer.questionHtml)}</dt>
										<dd class="mb-6 flex items-start justify-between gap-4">
											${striptags(answer.answerHtml)}
											${(this.report?.isDeterminedByTotalScore ?? false) && answer.score !== null
												? html`(${answer.score} punten)`
												: null}
											<button
												@click=${() => {
													this.openQuestion(answer.questionId);
												}}
												class="shrink-0 font-bold text-donkerblauw-100 underline hover:cursor-pointer print:hidden"
												aria-label="Vraag wijzigen"
											>
												Wijzigen
											</button>
										</dd>`,
							)}
						`,
					)}
				</dl>
			</article>
			<slot></slot>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'szw-ba-report': SzwBaReport;
	}
}
